import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'

const EASE = [0.16, 1, 0.3, 1]

/** sub/display size ratio — headline shrinks to this; line grows from this → 1 */
const SCALE_SUB = 0.48

const HEADLINE_IN_MS = 550
/** Second line enters after first is big */
const LINE_DELAY = 0.55
const SWAP_DUR = 0.9
const WOW_POP_DELAY = LINE_DELAY + SWAP_DUR * 0.55
const HOLD_MS = 3800
const EXIT_MS = 420
const CYCLE_MS = HEADLINE_IN_MS + LINE_DELAY * 1000 + SWAP_DUR * 1000 + HOLD_MS + EXIT_MS

/**
 * Split path so "WOW" / "WOW!" can spring-pop.
 */
function splitWow(text = '') {
  const m = String(text).match(/^(.*?)(\bWOW!?\.?)(\s*)$/i)
  if (!m) return { before: text, wow: null, after: '' }
  return { before: m[1], wow: m[2], after: m[3] }
}

/**
 * Normalize content → two full message sets.
 */
export function buildHeroPathSets({ dualPaths, headline, pathWow, pathOkay } = {}) {
  if (Array.isArray(dualPaths) && dualPaths.length >= 2) {
    return dualPaths.slice(0, 2).map((s, i) => ({
      key: s.key || (i === 0 ? 'wow' : 'okay'),
      headline: s.headline || '',
      line: s.line || s.path || '',
      variant: s.variant || (i === 0 ? 'wow' : 'okay'),
    }))
  }

  const okayHeadline =
    typeof pathOkay === 'object' && pathOkay?.headline
      ? pathOkay.headline
      : typeof pathOkay === 'object' && pathOkay?.line1
        ? pathOkay.line1
        : ''
  const okayLine =
    typeof pathOkay === 'object' && pathOkay?.line
      ? pathOkay.line
      : typeof pathOkay === 'object' && pathOkay?.line2
        ? pathOkay.line2
        : typeof pathOkay === 'string'
          ? pathOkay
          : ''

  return [
    {
      key: 'wow',
      headline: headline || '',
      line: pathWow || '',
      variant: 'wow',
    },
    {
      key: 'okay',
      headline: okayHeadline,
      line: okayLine,
      variant: 'okay',
    },
  ]
}

/**
 * Dual-path hero choreography (per set):
 * 1. First line fades in BIG (display size)
 * 2. Second line enters below — first shrinks to sub size, second scales up to display size
 * WOW path: pop on WOW after grow. Okay path: soft wiggle on second line after grow.
 * CTA stays outside.
 */
export default function HeroDualPathAnimation({
  dualPaths,
  headline,
  pathWow,
  pathOkay,
  headlineClass = '',
  accentClass = 'text-color-highlight',
  lineClass = '',
}) {
  const reduce = useReducedMotion()
  const sets = useMemo(
    () => buildHeroPathSets({ dualPaths, headline, pathWow, pathOkay }),
    [dualPaths, headline, pathWow, pathOkay]
  )
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    if (reduce || sets.length < 2) return undefined
    const id = setInterval(() => setPhase((p) => (p + 1) % sets.length), CYCLE_MS)
    return () => clearInterval(id)
  }, [reduce, sets.length])

  if (!sets.length || !sets[0]?.headline) return null

  // Settled visual: first = sub size, second = display size
  if (reduce) {
    return (
      <div data-testid="hero-dual-path" data-reduced="true" className="overflow-visible">
        {sets.map((s) => {
          const parts = s.variant === 'wow' ? splitWow(s.line) : null
          return (
            <div key={s.key} className="mb-10 last:mb-0 overflow-visible">
              <p
                className={`${lineClass} leading-snug max-w-3xl text-primary-on-dark/90`}
                data-testid="hero-path-headline"
              >
                {s.headline}
              </p>
              {s.line ? (
                <h1
                  className={`mt-4 ${headlineClass} leading-tight max-w-3xl ${
                    s.variant === 'wow' ? accentClass : ''
                  }`}
                  data-testid="hero-path-line"
                >
                  {parts?.wow ? (
                    <>
                      {parts.before}
                      <span className="inline-block font-medium">{parts.wow}</span>
                      {parts.after}
                    </>
                  ) : (
                    s.line
                  )}
                </h1>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  const active = sets[phase] || sets[0]
  const wowParts = active.variant === 'wow' ? splitWow(active.line) : null
  const isWow = active.variant === 'wow'

  return (
    <div
      data-testid="hero-dual-path"
      data-phase={active.key}
      className="overflow-visible"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={active.key}
          className="overflow-visible"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: -10, transition: { duration: EXIT_MS / 1000, ease: EASE } }}
          transition={{ duration: 0.28, ease: EASE }}
        >
          {/* Line 1: fade in BIG, then shrink to sub size when line 2 enters */}
          <motion.h1
            className={`${headlineClass} leading-tight max-w-3xl overflow-visible origin-left`}
            style={{ transformOrigin: 'left center' }}
            data-testid="hero-path-headline"
            initial={{ opacity: 0, y: 22, scale: 1 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: SCALE_SUB,
            }}
            transition={{
              opacity: { duration: HEADLINE_IN_MS / 1000, ease: EASE },
              y: { duration: HEADLINE_IN_MS / 1000, ease: EASE },
              scale: {
                delay: LINE_DELAY,
                duration: SWAP_DUR,
                ease: EASE,
              },
            }}
          >
            {active.headline}
          </motion.h1>

          {/* Line 2: enters at sub size, scales UP to the init display size of line 1 */}
          {active.line ? (
            <div className="mt-5 md:mt-6 max-w-3xl overflow-visible">
              <motion.p
                className={`${headlineClass} leading-tight overflow-visible origin-left ${
                  isWow ? accentClass : 'text-primary-on-dark'
                }`}
                style={{ transformOrigin: 'left center' }}
                data-testid="hero-path-line"
                initial={{ opacity: 0, x: -40, scale: SCALE_SUB }}
                animate={{
                  opacity: 1,
                  x: 0,
                  scale: 1,
                }}
                transition={{
                  delay: LINE_DELAY,
                  duration: SWAP_DUR,
                  ease: EASE,
                }}
              >
                {isWow && wowParts?.wow ? (
                  <>
                    {wowParts.before}
                    <motion.span
                      className="inline-block font-medium"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: [0.6, 1.14, 1], opacity: 1 }}
                      transition={{
                        delay: WOW_POP_DELAY,
                        duration: 0.5,
                        times: [0, 0.55, 1],
                        ease: EASE,
                      }}
                    >
                      {wowParts.wow}
                    </motion.span>
                    {wowParts.after}
                  </>
                ) : isWow ? (
                  active.line
                ) : (
                  <motion.span
                    className="inline-block"
                    initial={{ rotate: 0 }}
                    animate={{ rotate: [0, -2.2, 2.2, -1.2, 1.2, 0] }}
                    transition={{
                      delay: WOW_POP_DELAY,
                      duration: 0.65,
                      ease: EASE,
                    }}
                  >
                    {active.line}
                  </motion.span>
                )}
              </motion.p>
            </div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
